import React from 'react';
import { useNavigate } from 'react-router-dom';

import RefreshRoundedIcon from '@mui/icons-material/RefreshOutlined';

import OlcButton from '~/components/General/OlcButton';
import { buildContactQueryParams } from '~/utils/contactQueryParams';
// import OlcDataGrid from '~/components/General/OlcDataGrid';
// import FindNewContactsModal from '~/components/General/FindNewContactsModal';
// import { Add3CIcon } from '~/assets/images';

import { formatNumber, formatPercent, getLastRefreshedLabel } from '../../utils';

const CONTACT_FILTERS = {
  total: {
    isReturnAddress: false,
  },
  deliverable: {
    addressStatus: ['Verified', 'Corrected'],
    excludeDncList: true,
  },
  undeliverable: {
    addressStatus: ['Failed'],
    excludeDncList: true,
    isReturnAddress: false,
  },
  suppressed: {
    contactType: ['Do Not Contact'],
  },
  activeMailing: {
    activeMailing: true,
  },
};

const getContactsSearch = (filters = {}) => {
  const queryParams = buildContactQueryParams({ filters });
  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
      return;
    }

    searchParams.set(key, value);
  });

  return searchParams.toString();
};

const MetricCard = ({ label, value, helper, className = '', onClick, ariaLabel, disabled }) => {
  const isInteractive = Boolean(onClick) && !disabled;
  const Component = isInteractive ? 'button' : 'article';

  return (
    <Component
      type={isInteractive ? 'button' : undefined}
      className={`dashboard-metric-card ${isInteractive ? 'dashboard-metric-card--button' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
      onClick={isInteractive ? onClick : undefined}
      aria-label={isInteractive ? ariaLabel : undefined}
      aria-disabled={disabled || undefined}
    >
      <span>{label}</span>
      <strong>{value}</strong>
      {helper ? <small>{helper}</small> : null}
    </Component>
  );
};

const ContactsTab = ({ contacts, isRefreshing, onRefresh }) => {
  const navigate = useNavigate();
  const areCardsDisabled = contacts.isDashboardCardsDisabled === true;

  const handleContactsFilterClick = (filters) => {
    const search = getContactsSearch(filters);

    navigate({
      pathname: '/contacts',
      search: search ? `?${search}` : '',
    });
  };

  /*
  const navigate = useNavigate();
  const [isBuildAudienceOpen, setIsBuildAudienceOpen] = useState(false);
  const listColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'List Name',
        minWidth: 190,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <div className="dashboard-primary-cell">
            <strong>{row.name}</strong>
          </div>
        ),
        compactValueGetter: (row) => row.name,
      },
      {
        field: 'source',
        headerName: 'Source',
        minWidth: 160,
        flex: 0.9,
        sortable: false,
      },
      {
        field: 'contacts',
        headerName: 'Contacts',
        minWidth: 130,
        flex: 0.7,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: ({ value }) => formatNumber(value),
      },
      {
        field: 'deliverableRate',
        headerName: 'Deliverable',
        minWidth: 120,
        flex: 0.7,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: ({ value }) => formatPercent(value, { maximumFractionDigits: 2 }),
      },
      {
        field: 'timesMailed',
        headerName: 'Times Mailed',
        minWidth: 130,
        flex: 0.7,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: ({ value }) => formatNumber(value),
      },
      {
        field: 'lastMailed',
        headerName: 'Last Mailed',
        minWidth: 140,
        flex: 0.8,
        sortable: false,
      },
    ],
    []
  );
  */

  return (
    <>
      <header className="dashboard-section-header">
        <div>
          <h1>Audience Overview</h1>
          <p>Manage your audience and deliverability.</p>
        </div>
        <div className="dashboard-section-header-actions">
          <span>
            Last refreshed <strong>{getLastRefreshedLabel(contacts.lastRefreshedAt)}</strong>
          </span>
          <OlcButton
            type="button"
            variant="secondary"
            iconOnly
            className={`refresh-button ${isRefreshing ? 'is-refreshing' : ''}`}
            aria-label="Refresh contacts dashboard"
            onClick={onRefresh}
            disabled={isRefreshing}
            startIcon={<RefreshRoundedIcon />}
          >
            Refresh
          </OlcButton>
        </div>
      </header>

      <section
        className="dashboard-metric-grid dashboard-metric-grid--five"
        aria-label="Contacts overview"
      >
        <MetricCard
          label="Total contacts"
          value={formatNumber(contacts.total)}
          onClick={() => handleContactsFilterClick(CONTACT_FILTERS.total)}
          ariaLabel="View all contacts"
          disabled={areCardsDisabled}
        />
        <MetricCard
          label="Deliverable"
          value={formatPercent(contacts.deliverableRate, { maximumFractionDigits: 2 })}
          helper={`${formatNumber(contacts.deliverable)} address verified`}
          className="is-good"
          onClick={() => handleContactsFilterClick(CONTACT_FILTERS.deliverable)}
          ariaLabel="View deliverable contacts"
          disabled={areCardsDisabled}
        />
        <MetricCard
          label="On active mailings"
          value={formatNumber(contacts.onActiveMailings)}
          helper="currently in a send"
          onClick={() => handleContactsFilterClick(CONTACT_FILTERS.activeMailing)}
          ariaLabel="View contacts on active mailings"
          disabled={areCardsDisabled}
        />
        <MetricCard
          label="Undeliverable"
          value={formatNumber(contacts.undeliverable)}
          helper="bad or vacant address"
          className="is-warning"
          onClick={() => handleContactsFilterClick(CONTACT_FILTERS.undeliverable)}
          ariaLabel="View undeliverable contacts"
          disabled={areCardsDisabled}
        />
        <MetricCard
          label="Suppressed"
          value={formatNumber(contacts.suppressed)}
          helper="do-not-mail flags"
          onClick={() => handleContactsFilterClick(CONTACT_FILTERS.suppressed)}
          ariaLabel="View suppressed contacts"
          disabled={areCardsDisabled}
        />
      </section>

      {/*
      <section className="dashboard-table-section">
        <div className="section-heading">
          <div>
            <div className="heading-row">
              <h2>Most used lists</h2>
            </div>
            <p>The audiences you mail most. Click a list to view its contacts.</p>
          </div>
          <div className="section-actions">
            <OlcButton
              type="button"
              variant="primary"
              size="small"
              startIcon={<img src={Add3CIcon} alt="" />}
              onClick={() => setIsBuildAudienceOpen(true)}
            >
              Build a list
            </OlcButton>
          </div>
        </div>
        <div className="dashboard-grid-shell">
          <OlcDataGrid
            rows={contacts.mostUsedLists}
            columns={listColumns}
            hideFooter
            rowSelection={false}
            disableRowSelectionOnClick
            disableColumnMenu
            rowHeight={61}
            columnHeaderHeight={48}
            getRowId={(row) => row.id}
            onRowClick={() => navigate('/contacts')}
            slots={{
              noRowsOverlay: () => (
                <div className="dashboard-grid-empty">
                  Your most used lists will appear here once you build and mail an audience.
                </div>
              ),
            }}
          />
        </div>
      </section>

      <FindNewContactsModal
        open={isBuildAudienceOpen}
        onClose={() => setIsBuildAudienceOpen(false)}
      />
      */}
    </>
  );
};

export default ContactsTab;
